import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { BrandSignature } from "./BrandSignature";
import { ConsultationModal } from "./ConsultationModal";

const LINKS = [
  { to: "/",            label: "Home" },
  { to: "/stays",       label: "Stays" },
  { to: "/yatra",       label: "Yatra" },
  { to: "/weddings",    label: "Weddings" },
  { to: "/events",      label: "Events" },
  { to: "/experiences", label: "Experiences" },
];

// Pages whose hero starts with a full-bleed image — nav sits transparent on top
const TRANSPARENT_ROUTES = ["/", "/stay", "/stays", "/chardham", "/yatra", "/weddings", "/weddings/triyuginarayan", "/about", "/events", "/experiences"];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showConsult, setShowConsult] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => {
      const threshold = Math.max(200, window.innerHeight * 0.82);
      setScrolled(window.scrollY > threshold);
    };
    on(); window.addEventListener("scroll", on, { passive: true });
    window.addEventListener("resize", on);
    return () => { window.removeEventListener("scroll", on); window.removeEventListener("resize", on); };
  }, [pathname]);

  const transparentOverHero = TRANSPARENT_ROUTES.includes(pathname) && !scrolled && !open;
  const light = transparentOverHero;
  const phoneDisplay = "6395960333";
  const phoneHref = "tel:6395960333";

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 ${transparentOverHero ? "kc-nav-transparent" : "kc-nav-glass"}`}
        data-testid="site-nav"
      >
        <div className="max-w-[1500px] mx-auto flex items-center justify-between px-6 md:px-12 py-4 md:py-5">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 leading-none">
            <span className={`relative inline-flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden transition-shadow duration-500 ${light ? "shadow-[0_0_20px_rgba(212,175,55,0.35)]" : "shadow-[0_0_16px_rgba(212,175,55,0.18)]"}`}>
              <img src="/images/kc-logo-240.png" alt="THE KC INDIA" width="240" height="240" loading="eager" className="w-full h-full object-cover" />
            </span>
            <span className={`font-serif-display text-[20px] md:text-[24px] tracking-[0.01em] hidden sm:inline ${light ? "text-[#F8F6F2]" : "text-ink"}`}>
              THE <span className="italic text-gold">KC</span> INDIA
            </span>
          </Link>

          {/* Desktop primary nav */}
          <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
            {LINKS.map(l => (
              <NavLink
                key={l.to} to={l.to} end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `link-underline text-[11px] uppercase tracking-[0.28em] font-normal ${
                    isActive ? "text-gold active" : (light ? "text-[#F8F6F2] hover:text-gold" : "text-ink hover:text-gold")
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster · phone + consult CTA */}
          <div className="hidden md:flex items-center gap-4 xl:gap-5">
            <a
              href={phoneHref}
              className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] transition-colors ${light ? "text-[#F8F6F2] hover:text-gold" : "text-ink hover:text-gold"}`}
              data-testid="nav-call-btn"
              aria-label={`Call ${phoneDisplay}`}
            >
              <Phone size={13} strokeWidth={1.6} />
              <span className="hidden xl:inline">{phoneDisplay}</span>
            </a>
            <button
              onClick={() => setShowConsult(true)}
              className={light ? "btn-gold" : "btn-saffron"}
              data-testid="nav-consult-btn"
            >
              Book Free Consultation
            </button>
          </div>

          {/* Mobile · phone icon + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            <a
              href={phoneHref}
              className={`inline-flex items-center justify-center w-10 h-10 rounded-full border ${light ? "border-[#D4AF37]/50 text-[#F8F6F2]" : "border-kc text-ink"} hover:bg-gold hover:text-[#0F0F0F] transition-colors`}
              data-testid="nav-mobile-call"
              aria-label="Call"
            >
              <Phone size={15} strokeWidth={1.6} />
            </a>
            <button
              onClick={() => setShowConsult(true)}
              className={`hidden sm:inline-flex ${light ? "btn-gold" : "btn-saffron"} !py-2 !px-3 !text-[9px] !tracking-[0.22em]`}
              data-testid="nav-mobile-consult"
            >
              Consult
            </button>
            <button
              className={`w-10 h-10 flex items-center justify-center ${light ? "text-[#F8F6F2]" : "text-ink"}`}
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(v => !v)}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {open && (
          <div className="lg:hidden border-t border-kc bg-[#F8F6F2]/98 backdrop-blur-md" data-testid="nav-mobile-menu">
            <div className="px-6 py-6 flex flex-col gap-3">
              {LINKS.map(l => (
                <NavLink
                  key={l.to} to={l.to} end={l.to === "/"}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) => `py-2 text-[11px] uppercase tracking-[0.32em] ${isActive ? "text-gold" : "text-ink"}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-3 flex gap-3">
                <a href={phoneHref} className="btn-ghost flex-1 justify-center" data-testid="nav-mobile-menu-call">
                  <Phone size={13} strokeWidth={1.6} /> {phoneDisplay}
                </a>
              </div>
              <button
                onClick={() => { setOpen(false); setShowConsult(true); }}
                className="btn-saffron w-full justify-center mt-1"
                data-testid="nav-mobile-menu-consult"
              >
                Book Free Consultation
              </button>
              <div className="mt-6 pt-6 border-t border-kc flex justify-center">
                <BrandSignature size="sm" tone="ink" align="center" data-testid="mobile-menu-brand-signature" />
              </div>
            </div>
          </div>
        )}
      </header>

      <ConsultationModal open={showConsult} onClose={() => setShowConsult(false)} />
    </>
  );
};
